"use client";

import React from "react";
import { CircleCheck as CheckCircle2, Clock, ShieldCheck, Sparkles, Wrench, PhoneCall, ArrowRight, Eye, Waves } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const benefits = [{
  icon: ShieldCheck,
  title: "Prevents Severe Water Damage",
  desc: "Unresolved clogs create pipe pressure and sudden overflows. Routine clearing stops structural damage before it starts."
}, {
  icon: Sparkles,
  title: "Eliminates Odors & Bacteria",
  desc: "Organic waste, grease, and hair trapped in drains create foul smells. Complete cleaning sanitizes lines thoroughly."
}, {
  icon: Waves,
  title: "Restores Full Water Velocity",
  desc: "Remove mineral scale and debris accumulation so sinks, tubs, and main lines drain instantly without gurgling."
}, {
  icon: Clock,
  title: "Saves on Long-Term Repairs",
  desc: "Regular professional cleanouts extend the operational lifespan of your residential or commercial plumbing system."
}];
const processSteps = [{
  step: "01",
  title: "Video Camera Inspection",
  desc: "We insert high-definition pipe cameras to pinpoint the exact location and root cause of the blockage.",
  icon: Eye
}, {
  step: "02",
  title: "Targeted Clearing Method",
  desc: "Using motorized snaking or high-pressure hydro-jetting, we break down grease, roots, and hard blockages.",
  icon: Wrench
}, {
  step: "03",
  title: "Flow Verification & Cleanup",
  desc: "We conduct a complete drainage test to confirm unrestricted flow, leaving your work area spotless.",
  icon: CheckCircle2
}];
export default function DrainOverview() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="draincleaningoverview" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Top Header Block */}
        <div className="max-w-3xl mx-auto text-center space-y-4 mb-16">
          <Badge variant="secondary" className="px-3 py-1 font-medium text-xs tracking-wide uppercase">
            Specialized Drain Care
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight">
            Comprehensive Drain Cleaning in Jonesboro
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Slow drains and recurring backups signal deep buildup inside your waste lines. Our licensed local technicians use state-of-the-art tools to quickly locate blockages, scour pipe walls clean, and protect your home from sudden plumbing emergencies.
          </p>
        </div>

        {/* Main Content: Overview & Benefits */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start mb-16">
          {/* Detailed Narrative Section */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} variants={containerVariants} className="lg:col-span-5 space-y-6">
            <motion.div variants={itemVariants} className="space-y-4">
              <h3 className="text-2xl font-default font-semibold text-foreground">
                Why Standard Chemical Cleaners Aren't Enough
              </h3>
              <p className="text-muted-foreground leading-relaxed text-sm sm:text-base">
                Store-bought chemical drain cleaners often produce short-lived relief while corroding your pipes from the inside out. They rarely eliminate tough obstructions like tree root intrusion, dense grease collars, or compacted foreign objects.
              </p>
              <p className="text-muted-foreground leading-relaxed text-sm sm:text-base">
                Jonesboro Emergency Plumber provides heavy-duty mechanical snaking and eco-friendly hydro-jetting services designed to clear lines cleanly without degrading older cast iron, PVC, or copper lines.
              </p>
            </motion.div>

            <motion.div variants={itemVariants} className="p-6 rounded-xl bg-card border border-border shadow-xs space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-lg bg-primary/10 text-primary">
                  <Wrench className="size-5" />
                </div>
                <h4 className="font-semibold text-foreground">Emergency Support Ready 24/7</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                Standing water in your sinks, bathtubs, or basement? Our mobile response crew is stationed in Clayton County for same-day dispatch.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <Button asChild size="default" className="w-full sm:w-auto font-medium">
                  <a href="tel:4708479076" className="inline-flex items-center justify-center gap-2">
                    <PhoneCall className="size-4" />
                    <span>Call Now</span>
                  </a>
                </Button>
                <Button asChild variant="outline" size="default" className="w-full sm:w-auto">
                  <Link to="/contact/" className="inline-flex items-center justify-center gap-2">
                    <span>Book Inspection</span>
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>
              </div>
            </motion.div>
          </motion.div>

          {/* Benefits Grid */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} variants={containerVariants} className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            {benefits.map((item, i) => {
            const IconComp = item.icon;
            return <motion.div key={item.title} data-index={i} variants={itemVariants}>
                  <Card className="h-full border border-border shadow-xs hover:shadow-md transition-[box-shadow,border-color]">
                    <CardHeader className="space-y-2">
                      <div className="size-10 rounded-lg bg-secondary text-secondary-foreground flex items-center justify-center mb-1">
                        <IconComp className="size-5" />
                      </div>
                      <CardTitle className="text-base sm:text-lg font-default">{item.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-xs sm:text-sm leading-relaxed text-muted-foreground">
                        {item.desc}
                      </CardDescription>
                    </CardContent>
                  </Card>
                </motion.div>;
          })}
          </motion.div>
        </div>

        <Separator className="my-12" />

        {/* What to Expect Section */}
        <div className="space-y-10">
          <div className="text-center max-w-2xl mx-auto space-y-2">
            <h3 className="text-2xl sm:text-3xl font-default font-bold text-foreground">
              What to Expect During Your Service
            </h3>
            <p className="text-sm sm:text-base text-muted-foreground">
              A transparent, hassle-free process from the moment our licensed technician arrives at your door.
            </p>
          </div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} variants={containerVariants} className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {processSteps.map((item, i) => {
            const StepIcon = item.icon;
            return <motion.div key={item.step} data-index={i} variants={itemVariants}>
                  <div className="relative h-full p-6 rounded-xl bg-card border border-border shadow-xs flex flex-col justify-between group">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="size-11 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                          <StepIcon className="size-5" />
                        </div>
                        <span className="text-2xl font-mono font-bold text-muted-foreground/50">
                          {item.step}
                        </span>
                      </div>
                      <h4 className="text-lg font-default font-semibold text-foreground">
                        {item.title}
                      </h4>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                </motion.div>;
          })}
          </motion.div>
        </div>

        {/* Bottom Banner */}
        <div className="mt-16 p-6 sm:p-8 rounded-2xl bg-secondary border border-border flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="space-y-1 text-center sm:text-left">
            <h4 className="text-lg sm:text-xl font-default font-semibold text-secondary-foreground">
              Dealing with persistent slow drainage or foul pipe odors?
            </h4>
            <p className="text-sm text-muted-foreground">
              Speak with a Jonesboro drain specialist right now for honest quotes and same-day service.
            </p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <Button asChild size="lg" className="shadow-sm font-semibold">
              <Link to="/services/">
                <span>View All Services</span>
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}